import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "antd";
import ExtendedTimePicker from "@lhd/components/ExtendedTimePicker";
import type { LogTimeByDateResponse } from "@lhd/types";
import {
  useFetchWorkerLogtimePermissions,
  type WorkerLogtimesSaveJsonItem,
} from "@lhd/hooks";
import { formatIsoInTokyo } from "@lhd/features/TrackingTime/workLogTimezone";
import { EditorWrapper } from "./styled";
import {
  AttendanceDraftRow,
  AttendanceEditorDraft,
  PUNCH_TYPE_LABELS,
  PunchLogType,
  buildAttendanceSaveItems,
  createAttendanceEditorDraft,
  enrichLogtimesWithOriginalValues,
  formatAttendanceHistory,
  inferNextPunchType,
  validateAttendanceDraft,
} from "./utility";

import { user } from "@lhd/features/users/selector";
import { useSelector } from "react-redux";
import { useAttendanceManagementPermission } from "@lhd/features/TrackingTime/useAttendanceManagementPermission";
import { CHECK_IN_HOUR_COUNT, EXTENDED_TIME_HOUR_COUNT } from "../../../until";

type Props = {
  data: LogTimeByDateResponse;
  saving: boolean;
  serverError?: string;
  savedMessage?: string;
  onSave: (items: WorkerLogtimesSaveJsonItem[]) => Promise<void>;
  onCancel: () => void;
  onClearMessages: () => void;
};

const TYPE_OPTIONS = Object.entries(PUNCH_TYPE_LABELS) as Array<
  [PunchLogType, string]
>;

const isOutsideCheckInTimeRange = (time: string) => {
  const hour = Number(time.split(":")[0]);
  return Number.isInteger(hour) && hour >= CHECK_IN_HOUR_COUNT;
};

const InlineAttendanceEditor: FC<Props> = ({
  data,
  saving,
  serverError,
  savedMessage,
  onSave,
  onCancel,
  onClearMessages,
}) => {
  const enrichedData = useMemo<LogTimeByDateResponse>(
    () => enrichLogtimesWithOriginalValues(data),
    [data],
  );

  const [draft, setDraft] = useState<AttendanceEditorDraft>(() =>
    createAttendanceEditorDraft(enrichedData),
  );

  const getOptions = (currentRow: AttendanceDraftRow) => {
    const hasCheckInLog = draft.rows.some(
      (row) => row.logType === "check_in" && !row.deleted,
    );

    const hasCheckOutLog = draft.rows.some(
      (row) => row.logType === "check_out" && !row.deleted,
    );

    return TYPE_OPTIONS.map(([value, label]) => ({
      value,
      label,
      disabled:
        (value === "check_in" && hasCheckInLog) ||
        (value === "check_out" && hasCheckOutLog),
    }));
  };

  const [clientErrors, setClientErrors] = useState<string[]>([]);
  const newRowSequence = useRef(0);

  const auth = useSelector(user);

  const { canManageWorkersAttendance } = useAttendanceManagementPermission();

  const shouldFetchLogtimePermissions =
    Boolean(auth) && !canManageWorkersAttendance;
  const { data: logtimePermissions } = useFetchWorkerLogtimePermissions(
    shouldFetchLogtimePermissions,
  );

  const canEdit =
    logtimePermissions?.logtime_edit?.includes(data?.worker_id) ||
    canManageWorkersAttendance;

  useEffect(() => {
    setDraft(createAttendanceEditorDraft(enrichedData));
    setClientErrors([]);
  }, [enrichedData]);

  const updateRow = (key: string, patch: Partial<AttendanceDraftRow>) => {
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row) =>
        row.key === key ? { ...row, ...patch } : row,
      ),
    }));
    setClientErrors([]);
    onClearMessages();
  };

  const addRow = () => {
    setDraft((current) => {
      newRowSequence.current += 1;
      return {
        ...current,
        rows: [
          ...current.rows,
          {
            key: `new-${newRowSequence.current}`,
            logType: inferNextPunchType(current.rows),
            time: "",
            deleted: false,
          },
        ],
      };
    });
    setClientErrors([]);
    onClearMessages();
  };

  const removeOrRestoreRow = (row: AttendanceDraftRow) => {
    if (row.id == null) {
      setDraft((current) => ({
        ...current,
        rows: current.rows.filter((item) => item.key !== row.key),
      }));
      setClientErrors([]);
      onClearMessages();
      return;
    }
    updateRow(row.key, { deleted: !row.deleted });
  };

  const submit = async () => {
    const errors = validateAttendanceDraft(data.date, draft.rows);
    if (errors.length > 0) {
      setClientErrors(errors);
      return;
    }
    setClientErrors([]);
    const items = buildAttendanceSaveItems(enrichedData, draft);
    if (items.length === 0) {
      setClientErrors(["変更された打刻またはメモはありません。"]);
      return;
    }
    await onSave(items);
  };

  return (
    <EditorWrapper data-testid="attendance-inline-editor">
      <div className="attendance-editor-header">
        <div className="attendance-editor-title">
          {data.worker_name}・{data.date} の打刻を一覧内で編集
        </div>
        <span>元の打刻は修正履歴に保存されます</span>
      </div>
      <div className="attendance-editor-guide">
        時刻は分単位です。未来時刻と不正な打刻順序は保存できません。
      </div>

      {clientErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={clientErrors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        />
      )}
      {serverError && <Alert type="error" showIcon message={serverError} />}
      {savedMessage && <Alert type="success" showIcon message={savedMessage} />}

      <div aria-label="打刻編集行">
        {draft.rows.map((row, index) => (
          <div
            className={`attendance-editor-grid${
              row.deleted ? " attendance-editor-grid--deleted" : ""
            }`}
            data-testid="attendance-log-row"
            data-logtime-id={row.id ?? "new"}
            key={row.key}
          >
            <select
              aria-label={`打刻種別 ${index + 1}`}
              disabled={row.deleted || saving || !canEdit}
              value={row.logType}
              onChange={(event) => {
                const logType = event.target.value as PunchLogType;
                const shouldResetTime =
                  logType === "check_in" && isOutsideCheckInTimeRange(row.time);
                updateRow(row.key, {
                  logType,
                  ...(shouldResetTime ? { time: "00:00" } : {}),
                });
              }}
            >
              {getOptions(row).map(({ value, label, disabled }) => (
                <option value={value} key={value} disabled={disabled}>
                  {label}
                </option>
              ))}
            </select>
            <ExtendedTimePicker
              className="attendance-editor-time-picker"
              aria-label={`打刻時刻 ${index + 1}`}
              disabled={row.deleted || saving || !canEdit}
              extendedTimeHourCount={
                row.logType === "check_in"
                  ? CHECK_IN_HOUR_COUNT
                  : EXTENDED_TIME_HOUR_COUNT
              }
              value={row.time}
              onChange={(time) => updateRow(row.key, { time })}
            />
            <span className="attendance-editor-original">
              {row.id != null && row.originalTime
                ? `元の打刻: ${
                    PUNCH_TYPE_LABELS[row.originalLogType ?? row.logType]
                  } ${row.originalTime}`
                : "新しい打刻"}
            </span>
            <Button
              danger={!row.deleted}
              disabled={saving || !canEdit}
              size="small"
              aria-label={`打刻 ${index + 1} を${
                row.deleted ? "復元" : "削除"
              }`}
              onClick={() => removeOrRestoreRow(row)}
            >
              {row.deleted ? "元に戻す" : "削除"}
            </Button>
          </div>
        ))}
      </div>

      <textarea
        aria-label="日次メモ"
        disabled={saving}
        placeholder="ＭＥＭＯ（修正理由等を入力）"
        value={draft.memo}
        onChange={(event) => {
          setDraft((current) => ({ ...current, memo: event.target.value }));
          setClientErrors([]);
          onClearMessages();
        }}
      />

      <div className="attendance-editor-actions">
        <Button disabled={saving || !canEdit} onClick={addRow}>
          打刻を追加
        </Button>
        <div className="attendance-editor-actions-right">
          <Button disabled={saving} onClick={onCancel}>
            閉じる
          </Button>
          <Button
            type="primary"
            loading={saving}
            data-testid="attendance-save"
            onClick={submit}
          >
            変更を保存
          </Button>
        </div>
      </div>

      <details
        className="attendance-editor-history"
        data-testid="attendance-change-history"
      >
        <summary>修正履歴（{data.change_histories?.length ?? 0}件）</summary>
        {(data.change_histories?.length ?? 0) === 0 ? (
          <div>修正履歴はありません。</div>
        ) : (
          <ul>
            {data.change_histories.map((history) => (
              <li key={history.id} data-testid="attendance-history-item">
                <span>{formatAttendanceHistory(history)}</span>
                <span className="attendance-editor-history-meta">
                  {history.changed_by_full_name || "修正者不明"} /{" "}
                  {formatIsoInTokyo(history.created_at, "YYYY/MM/DD HH:mm")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </details>
    </EditorWrapper>
  );
};

export default InlineAttendanceEditor;
